import React, { useState } from "react";
import {
  Table,
  THead,
  TBody,
  TR,
  TH,
  TD,
  ActionButton,
  LoadingRow,
  EmptyRow,
} from "components/ui/Table";
import {
  PencilSquareIcon,
  TrashIcon,
  CheckIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import Modal from "components/ui/modals";
import Pagination from "components/ui/Pagination";
import InputSearch from "components/ui/InputSearch";
import Input from "components/ui/Input";
import Select from "components/ui/Select";
import SetSchemeMaster from "components/schemeMaster/SetSchemeMaster";
import DeleteSchemeMaster from "components/schemeMaster/DeleteSchemeMaster";
import { useAllSchemeMasters } from "http-hooks/schemeMaster/useSchemeMaster";
import useAuth from "hooks/useAuth";

// Constants moved to top to prevent re-renders
const statusOptions = [
  { value: "", label: "All Status" },
  { value: "1", label: "Active" },
  { value: "0", label: "Inactive" },
];

const schemeTypeOptions = [
  { value: "", label: "All Types" },
  { value: "R", label: "Retail" },
  { value: "W", label: "Wholesale" },
  { value: "C", label: "Case" },
];

const initialFilter = {
  sch_name: "",
  vendor: "",
  schmtp: "",
  ag_status: "",
  page: 1,
  limit: 20,
};

export default function SchemeMasterPage() {
  const { agttp } = useAuth();
  const [filter, setFilter] = useState(initialFilter);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [editSchemeId, setEditSchemeId] = useState(null);
  const [deleteSchemeId, setDeleteSchemeId] = useState(null);

  const { data, isLoading } = useAllSchemeMasters(filter);

  const schemes = data?.data || [];
  const currentPage = data?.page || 1;
  const totalPages = data?.totalPages || 1;

  const handleSearch = (searchValue) => {
    setFilter({ ...filter, sch_name: searchValue, page: 1 });
  };

  const handlePageChange = (newPage) => {
    setFilter({ ...filter, page: newPage });
  };

  const handleFilterChange = (key, value) => {
    setFilter({ ...filter, [key]: value, page: 1 });
  };

  const handleAddScheme = () => {
    setIsAddModalOpen(true);
  };

  const handleEditScheme = (id) => {
    setEditSchemeId(id);
  };

  const handleDeleteScheme = (id) => {
    setDeleteSchemeId(id);
  };

  const getSchemeTypeLabel = (type) => {
    switch (type) {
      case "R":
        return "Retail";
      case "W":
        return "Wholesale";
      case "C":
        return "Case";
      default:
        return type;
    }
  };

  const getFormTypeLabel = (type) => {
    switch (type) {
      case "S":
        return "Sales";
      case "P":
        return "Purchases";
      case "G":
        return "General";
      default:
        return type;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB");
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      {/* Header */}
      <div className="sm:flex sm:items-center mb-6">
        <div className="sm:flex-auto">
          <h1 className="font-semibold leading-6 text-3xl text-gray-900">
            Scheme Master
          </h1>
        </div>
        <div className="mt-4 sm:ml-16 sm:mt-0 sm:flex-none">
          <button
            type="button"
            onClick={handleAddScheme}
            className="block rounded-md bg-gray-600 px-3 py-2 text-center text-sm font-semibold text-white shadow-sm hover:bg-gray-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-600"
          >
            Add Scheme
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <div className="bg-white rounded-lg p-3">
          <InputSearch
            onSearch={handleSearch}
            label="Search by Name"
            placeholder="Scheme name"
          />
        </div>

        <div className="bg-white rounded-lg p-3">
          <Input
            label="Vendor Code"
            type="text"
            value={filter.vendor}
            onChange={(e) => handleFilterChange("vendor", e.target.value)}
            placeholder="Enter vendor code"
          />
        </div>

        <div className="bg-white rounded-lg p-3">
          <Select
            label="Scheme Type"
            options={schemeTypeOptions}
            value={schemeTypeOptions.find((opt) => opt.value === filter.schmtp)}
            onChange={(option) =>
              handleFilterChange("schmtp", option ? option.value : "")
            }
            placeholder="All Types"
            isClearable={true}
          />
        </div>

        <div className="bg-white rounded-lg p-3">
          <Select
            label="Status"
            options={statusOptions}
            value={statusOptions.find((opt) => opt.value === filter.ag_status)}
            onChange={(option) =>
              handleFilterChange("ag_status", option ? option.value : "")
            }
            placeholder="All Status"
            isClearable={true}
          />
        </div>
      </div>

      {/* Table */}
      <Table>
        <THead>
          <TR>
            <TH first>Type</TH>
            {agttp === 1 && <TH>Form Type</TH>}
            <TH>Name</TH>
            <TH>Vendor</TH>
            <TH>Announced Date</TH>
            <TH>From Date</TH>
            <TH>To Date</TH>
            <TH>Circular Date</TH>
            <TH>Status</TH>
            <TH last>
              <span className="sr-only">Actions</span>
            </TH>
          </TR>
        </THead>
        <TBody>
          {isLoading ? (
            <LoadingRow colSpan={agttp === 1 ? 10 : 9} />
          ) : schemes.length === 0 ? (
            <EmptyRow colSpan={agttp === 1 ? 10 : 9} />
          ) : (
            schemes.map((scheme) => (
              <TR key={scheme.schemeid}>
                <TD first>{getSchemeTypeLabel(scheme.schmtp)}</TD>
                {agttp === 1 && <TD>{getFormTypeLabel(scheme.typ)}</TD>}
                <TD>{scheme.sch_name}</TD>
                <TD>{scheme.vendorDetail?.name || ""}</TD>
                <TD>{formatDate(scheme.sch_date)}</TD>
                <TD>{formatDate(scheme.sch_fm)}</TD>
                <TD>{formatDate(scheme.sch_to)}</TD>
                <TD>{formatDate(scheme.circlr_date)}</TD>
                <TD>
                  {scheme.ag_status === 1 ? (
                    <CheckIcon
                      className="h-5 w-5 text-green-600"
                      aria-hidden="true"
                    />
                  ) : (
                    <XMarkIcon
                      className="h-5 w-5 text-red-600"
                      aria-hidden="true"
                    />
                  )}
                </TD>
                <TD last>
                  <ActionButton
                    variant="primary"
                    onClick={() => handleEditScheme(scheme.schemeid)}
                    title="Edit Scheme"
                  >
                    <PencilSquareIcon className="h-4 w-4" aria-hidden="true" />
                    <span className="sr-only">
                      Edit scheme, {scheme.sch_name}
                    </span>
                  </ActionButton>
                  <ActionButton
                    variant="danger"
                    onClick={() => handleDeleteScheme(scheme.schemeid)}
                    title="Delete Scheme"
                    className="mr-0"
                  >
                    <TrashIcon className="h-4 w-4" aria-hidden="true" />
                    <span className="sr-only">
                      Delete scheme, {scheme.sch_name}
                    </span>
                  </ActionButton>
                </TD>
              </TR>
            ))
          )}
        </TBody>
      </Table>

      {/* Pagination */}
      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={handlePageChange}
        showPageJump={true}
      />

      {/* Add/Edit Modal */}
      <Modal
        open={isAddModalOpen || !!editSchemeId}
        setOpen={(val) => {
          if (!val) {
            setIsAddModalOpen(false);
            setEditSchemeId(null);
          }
        }}
      >
        <SetSchemeMaster
          id={editSchemeId}
          onClose={() => {
            setIsAddModalOpen(false);
            setEditSchemeId(null);
          }}
        />
      </Modal>

      {/* Delete Modal */}
      {deleteSchemeId && (
        <Modal
          open={!!deleteSchemeId}
          setOpen={(val) => !val && setDeleteSchemeId(null)}
        >
          <DeleteSchemeMaster
            id={deleteSchemeId}
            onClose={() => setDeleteSchemeId(null)}
          />
        </Modal>
      )}
    </div>
  );
}
